/**
 * PrivilegedChangeModal — founder security-code gate for granting Admin,
 * C-Suite or Operations (as a role or a portal membership).
 *
 * Creates role_change_requests/{id}; the privilegedRoleCodeSend function emails
 * a 6-digit code to the founder, and privilegedRoleCodeVerify applies the change
 * server-side once the right code is entered here. The client never writes the
 * privileged role itself.
 *
 * Props: change ({ targetUid, targetName, changeType: 'role'|'portal_add', value }),
 *        requestedBy ({ uid, name }), onApplied(), onClose()
 */
import { useState, useEffect, useRef } from 'react';
import { getApp } from 'firebase/app';
import { getFirestore, collection, addDoc, doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';

export const PRIVILEGED_ROLES = ['admin', 'c_suite', 'ops'];
export const FOUNDER_EMAIL = 'praveenkumar.n@spacemancraft.in';
const LABELS = { admin: 'Admin', c_suite: 'C-Suite', ops: 'Operations' };

const db = () => getFirestore(getApp());
const MONO = 'monospace';

export default function PrivilegedChangeModal({ change, requestedBy, onApplied, onClose }) {
  const [reqId,  setReqId]  = useState(null);
  const [req,    setReq]    = useState(null);
  const [code,   setCode]   = useState('');
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState('');
  const [round,  setRound]  = useState(0); // bump to resend
  const appliedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setReq(null); setReqId(null); setErr(''); setCode('');
    addDoc(collection(db(), 'role_change_requests'), {
      targetUid: change.targetUid, targetName: change.targetName || '',
      changeType: change.changeType || 'role', value: change.value,
      requestedByUid: requestedBy?.uid || null, requestedByName: requestedBy?.name || 'Admin',
      status: 'pending', createdAt: serverTimestamp(),
    }).then(ref => { if (!cancelled) setReqId(ref.id); })
      .catch(e => { if (!cancelled) setErr(`Could not start verification: ${e.message}`); });
    return () => { cancelled = true; };
  }, [round]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!reqId) return undefined;
    return onSnapshot(doc(db(), 'role_change_requests', reqId), snap => {
      const d = snap.data();
      setReq(d);
      if (d?.codeAttempt == null) setBusy(false);
      if (d?.status === 'applied' && !appliedRef.current) {
        appliedRef.current = true;
        setTimeout(() => onApplied?.(), 900);
      }
    }, () => {});
  }, [reqId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit(e) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) { setErr('Enter the 6-digit code'); return; }
    setErr(''); setBusy(true);
    try {
      await updateDoc(doc(db(), 'role_change_requests', reqId), { codeAttempt: code.trim(), codeAttemptAt: serverTimestamp() });
      setCode('');
    } catch (ex) { setErr(ex.message); setBusy(false); }
  }

  const status = req?.status || 'pending';
  const label = LABELS[change.value] || change.value;
  const what = change.changeType === 'portal_add'
    ? <>add <b>{change.targetName}</b> to the <b>{label}</b> portal</>
    : <>give <b>{change.targetName}</b> the <b>{label}</b> role</>;
  const ended = ['expired', 'locked', 'error'].includes(status);

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position:'fixed', inset:0, background:'#000000cc', zIndex:900, display:'flex',
        alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ width:'100%', maxWidth:420, background:'#0c112e', border:'1px solid #f59e0b66',
        borderRadius:10, padding:'22px 22px 18px', color:'#dee0ff', fontFamily:"'Inter',system-ui,sans-serif" }}>
        <div style={{ fontFamily:MONO, fontSize:9, letterSpacing:2, color:'#f59e0b', marginBottom:6 }}>🔐 FOUNDER VERIFICATION</div>
        <div style={{ fontSize:14, lineHeight:1.6, marginBottom:14 }}>You're about to {what}.</div>

        {status === 'pending' && !err && (
          <div style={{ fontFamily:MONO, fontSize:11, color:'#94a3b8' }}>Sending security code…</div>
        )}

        {status === 'code_sent' && (
          <form onSubmit={submit}>
            <div style={{ fontSize:12, color:'#94a3b8', marginBottom:10, lineHeight:1.6 }}>
              A 6-digit code was emailed to <b style={{ color:'#fff' }}>{FOUNDER_EMAIL}</b>. It expires in 10 minutes.
            </div>
            <input autoFocus value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric" placeholder="••••••"
              style={{ width:'100%', boxSizing:'border-box', textAlign:'center', fontFamily:MONO, fontSize:24,
                letterSpacing:10, padding:'10px', background:'#060b18', border:'1px solid #2e3351',
                color:'#fff', outline:'none', borderRadius:6, marginBottom:10 }} />
            <button type="submit" disabled={busy || code.length !== 6}
              style={{ width:'100%', padding:'10px', fontFamily:MONO, fontSize:11, letterSpacing:1, cursor:'pointer',
                background:'#f59e0b22', border:'1px solid #f59e0b', color:'#f59e0b', borderRadius:6,
                opacity: code.length === 6 ? 1 : 0.5 }}>
              {busy ? 'VERIFYING…' : 'VERIFY & APPLY'}
            </button>
          </form>
        )}

        {status === 'applied' && (
          <div style={{ fontFamily:MONO, fontSize:12, color:'#10b981' }}>✓ Verified — change applied.</div>
        )}

        {ended && (
          <div style={{ fontFamily:MONO, fontSize:11, color:'#f43f5e', marginBottom:10 }}>
            {status === 'expired' ? 'The code expired.' : status === 'locked' ? 'Too many wrong codes — request locked.' : (req?.error || 'Verification failed.')}
          </div>
        )}

        {(err || (req?.error && status === 'code_sent')) && (
          <div style={{ fontFamily:MONO, fontSize:10, color:'#f43f5e', marginTop:8 }}>{err || req.error}</div>
        )}

        <div style={{ display:'flex', justifyContent:'space-between', marginTop:16 }}>
          {(ended || status === 'code_sent') ? (
            <button type="button" onClick={() => { appliedRef.current = false; setRound(r => r + 1); }}
              style={{ background:'none', border:'none', color:'#65d4fa', fontFamily:MONO, fontSize:10, cursor:'pointer', padding:0 }}>
              ↻ SEND A NEW CODE
            </button>
          ) : <span />}
          <button type="button" onClick={onClose}
            style={{ background:'none', border:'1px solid #2e3351', color:'#94a3b8', fontFamily:MONO, fontSize:10,
              padding:'6px 14px', cursor:'pointer', borderRadius:4 }}>
            {status === 'applied' ? 'CLOSE' : 'CANCEL'}
          </button>
        </div>
      </div>
    </div>
  );
}
